import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { PageHeader, Form, Input, Button, Cascader, Spin, message } from 'antd';

import * as ToolRequest from '../../tools/ToolRequest';
import * as ToolPermission from '../../tools/ToolPermission';

import SpinFull from '../../components/SpinFull';

const formLayout = {
  labelCol: {
    sm: { span: 5 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
  labelWrap: true,
};

const formTailLayout = {
  wrapperCol: {
    sm: { offset: 5, span: 16 },
  },
};

export default function UserAddScreen() {
  const history = useHistory();
  const userData = useSelector(state => state.user);
  const userPermissionsData = useSelector(state => state.userPermissions);

  const canCreateUser = ToolPermission.hasPermission(userPermissionsData, 'ui:user', 'post');
  const isAllowedCrossCompany = ToolPermission.hasPermission(userPermissionsData, 'ui:user', 'post:xc');

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);

  useEffect(() => {
    (async () => {
      if (isAllowedCrossCompany) {
        const companyRes = await ToolRequest.request('GET', '/v1/company', {
          count: 1000,
        });

        setCompanyOptions(companyRes.data
          .filter(company => company.name !== '*')
          .map(company => ({
            value: company.id,
            label: `${company.displayName} (${company.name})`,
          })));
      }

      setLoaded(true);
    })();
  }, [isAllowedCrossCompany]);

  const onFinish = async (values) => {
    setLoading(true);

    try {
      await ToolRequest.request('POST', '/v1/user', {
        companyId: isAllowedCrossCompany ? values.companyId[0] : userData.companyId,
        displayName: values.displayName.trim(),
        name: values.name.trim(),
        password: values.password,
        roles: 'ADMIN',
      });

      message.success('Create User successfully');

      history.push('/user');
    } catch (err) {
      setLoading(false);

      if (err?.response?.data) {
        switch (err.response.data) {
          case 'DUPLICATE_NAME_IN_SAME_COMPANY':
            return message.error('Username is duplicate in this company');
          case 'CREATE_ROOT_COMPANY_USER_NOT_ALLOWED':
            return message.error('Cannot create user in root company');
          case 'COMPANY_NOT_FOUND':
            return message.error('Company not found');
          case 'INVALID_PARAMS':
            return message.error('Invalid parameters. Please check your inputs');
          default:
            return message.error('Unknown errors. Please retry');
        }
      }

      message.error('Unknown error. Please retry');
    }
  };

  if (!canCreateUser) {
    return <SpinFull />;
  }

  return loaded ? (
    <PageHeader
      title="Add a User"
      onBack={() => history.push('/user')}
    >
      <Spin spinning={loading}>
        <Form
          {...formLayout}
          onFinish={onFinish}
        >
          {isAllowedCrossCompany ? (
            <Form.Item
              label="Company"
              name="companyId"
              rules={[
                {
                  required: true,
                  message: 'Please select a company',
                },
              ]}
            >
              <Cascader
                options={companyOptions}
                placeholder="Select company"
              />
            </Form.Item>
          ) : null}

          <Form.Item
            label="Display Name"
            name="displayName"
            rules={[
              {
                required: true,
                message: 'Please enter the display name',
              },
            ]}
          >
            <Input
              placeholder="Display name"
            />
          </Form.Item>

          <Form.Item
            label="Username"
            name="name"
            rules={[
              {
                required: true,
                message: 'Please enter the username',
              },
            ]}
          >
            <Input
              placeholder="Login username"
            />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              {
                required: true,
                message: 'Please enter the password',
              },
            ]}
          >
            <Input.Password
              placeholder="Password"
            />
          </Form.Item>

          <Form.Item
            label="Role"
          >
            <Input disabled value="ADMIN" />
          </Form.Item>

          <Form.Item {...formTailLayout}>
            <Button type="primary" htmlType="submit" block>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </PageHeader>
  ) : <SpinFull />;
}
